import { Box, Divider, List, Stack, Typography } from '@ethings/ui-toolkit';
import { styled } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, useAuthenticated } from '../../auth';
import { useSwitchAwareConfig } from '../../hooks';
import { useHasAuthorization } from '../../util';
import { SideMenuItem } from './side-menu-item';
import { SideMenuItemGroupDefinition } from './side-menu.types';
import { isSideMenuItemVisible, sideMenuTransitionStyles } from './side-menu.utils';

interface SideMenuMenuItemsProps {
  menuItemGroups: SideMenuItemGroupDefinition[];
  isOpen: boolean;
}

export const SideMenuMenuItems: FC<SideMenuMenuItemsProps> = ({ menuItemGroups, isOpen }) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const { state } = useAuth();
  const config = useSwitchAwareConfig();
  const { hasAuthorization } = useHasAuthorization(state);
  const { account } = useAuthenticated();

  return (
    <Box>
      {menuItemGroups.map((menuItemGroup, i) => {
        const visibleMenuItems = menuItemGroup.menuItems.filter((menuItem) => isSideMenuItemVisible(menuItem, account, flags, hasAuthorization, config));

        if (visibleMenuItems.length === 0) {
          return null;
        }

        return (
          <Box my={1} key={i}>
            <Box position="relative">
              <MenuItemGroupLabel style={{ opacity: isOpen ? 1 : 0 }} variant="caption" component="span">{t(menuItemGroup.label)}</MenuItemGroupLabel>
              <StyledDivider style={{ opacity: isOpen ? 0 : 1 }} />
            </Box>
            <List>
              <Stack spacing={0.5}>
                {visibleMenuItems.map((menuItem, i) => (
                  <SideMenuItem key={i} isOpen={isOpen} menuItem={menuItem} />
                ))}
              </Stack>
            </List>
          </Box>
        );
      })}
    </Box>
  );
};

const MenuItemGroupLabel = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.sidebar.contrastText,
  ...sideMenuTransitionStyles,
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  top: '50%',
  borderColor: `${theme.sidebar.divider} !important`,
  ...sideMenuTransitionStyles,
}));

